import { LockOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Tabs,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import type { CurrentUser } from "../../api/currentUser";
import { ApiProblem } from "../../api/httpClient";
import { useApiResource } from "../../api/useApiResource";
import { useActorContext } from "../../auth/ActorContext";
import { can } from "../../auth/effectivePermissions";
import { EmptyState } from "../../components/EmptyState";
import { ErrorState } from "../../components/ErrorState";
import { ForbiddenState } from "../../components/ForbiddenState";
import { LoadingSkeleton } from "../../components/LoadingSkeleton";
import { StaleState } from "../../components/StaleState";
import { StatusBadge } from "../../components/StatusBadge";
import { useCatalog } from "../catalogs/useCatalog";
import { createGraveMartyrLink, type CreateGraveMartyrLinkInput } from "../graves/graveApi";
import {
  MARTYR_TAB_ORDER,
  getMartyr,
  getMartyrTab,
  type MartyrData,
  type MartyrTabKey,
  type MartyrTabVariant,
} from "./martyrApi";
import {
  confidenceStatusLabel,
  documentRoleLabel,
  documentTypeLabel,
  identityStatusLabel,
  identityStatusTone,
  linkTypeLabel,
  linkTypeTone,
  publicStatusLabel,
  relationshipTypeLabel,
} from "./martyrVocabulary";
import { formatDateVn, formatDateTimeVn, DATE_PICKER_MASK_FORMAT } from "../../lib/dateFormat";
import "./martyrs.css";

/**
 * SCR-026 — Hồ sơ liệt sĩ (S11.6, mockup D02, Spec §5 IRM row SCR-026).
 *
 * Ba lớp hiển thị, đúng thứ tự disclosure:
 *
 * 1. **Lớp mặc định** (không bao giờ bị đẩy vào sau tab): header nghiệp vụ, cảnh báo
 *    hạn chế/trạng thái hồ sơ, tổng quan field thiết yếu và action đề xuất liên kết mộ.
 * 2. **Lớp lũy tiến**: sáu tab `MARTYR_TAB_ORDER` — mỗi pane là một subcomponent TỰ
 *    nạp dữ liệu của chính nó (`useApiResource`), nên tab chưa mở không phát sinh
 *    request nào; đổi tab/hồ sơ sẽ unmount pane cũ và hủy request đang chạy.
 * 3. **Lớp không có chủ sở hữu**: `search-missions`/`dna`/`public-history` chưa có nguồn
 *    dữ liệu — hiển thị empty trung thực, KHÔNG gọi API và KHÔNG bịa dòng.
 *
 * `id` trên đường dẫn là UUID định tuyến kỹ thuật — KHÔNG bao giờ được render; mã
 * nghiệp vụ (`martyrCode`) mới lên header. Mọi nhãn trạng thái dùng từ vựng Task 5;
 * mã ngoài từ vựng hiển thị nguyên văn (không tự suy diễn ý nghĩa).
 *
 * UI **không** phải lớp bảo mật: `can()` chỉ quyết định có render action hay không,
 * backend vẫn deny trực tiếp. Không có đường tạo liên kết `CONFIRMED`/`COLLECTIVE`,
 * không sửa movement/correction/DNA, không hiển thị audit thô.
 */

/** Nhãn tab nghiệp vụ — khóa lấy đúng whitelist `MARTYR_TAB_ORDER`. */
const TAB_LABELS: Readonly<Record<MartyrTabKey, string>> = {
  documents: "Nguồn hồ sơ",
  relatives: "Thân nhân",
  "search-missions": "Tìm kiếm",
  graves: "Mộ",
  dna: "ADN",
  "public-history": "Lịch sử công khai",
};

/**
 * Giá trị điều khiển của `Tabs` khi chưa nhóm thông tin nào được mở.
 *
 * AntD mặc định chọn tab đầu tiên, nghĩa là pane đầu sẽ nạp ngay khi vào trang. SCR-026
 * yêu cầu tab chưa mở phát sinh **zero** request, nên `activeKey` được điều khiển bằng
 * giá trị "chưa mở tab" này — không khớp `key` của item nào nên không pane nào active,
 * và đây là hằng số nội bộ, không bao giờ lên DOM.
 */
const NO_TAB_ACTIVE = "__chua-mo-tab__";

/** Ba tab có nguồn dữ liệu (owner S11.2) — ba tab còn lại luôn rỗng trung thực. */
type OwnedTabKey = "documents" | "relatives" | "graves";

/** Dữ liệu union đã thu hẹp cho nhóm tab có nguồn; switch exhaustive theo `tab`. */
type OwnedTabData = MartyrTabVariant<OwnedTabKey>;
type DocumentRow = MartyrTabVariant<"documents">["rows"][number];
type RelativeRow = MartyrTabVariant<"relatives">["rows"][number];
type GraveRow = MartyrTabVariant<"graves">["rows"][number];


/** Giá trị văn bản rỗng/null hiển thị gạch dài — không bao giờ render "null"/"undefined". */
function textOrDash(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? "—" : trimmed;
}

const DASH = <Typography.Text type="secondary">—</Typography.Text>;

/** Cảnh báo lớp mặc định — suy TRỰC TIẾP từ field contract hiện có, không bịa lý do. */
type RecordAlert = Readonly<{
  tone: "warning" | "info";
  message: string;
  description: string;
}>;

/**
 * Cảnh báo pháp lý/nghi vấn của hồ sơ, chỉ từ field có thật trên `MartyrData`:
 * `publicStatus === "RESTRICTED"` và ba trạng thái danh tính cần người dùng biết ngay.
 * Nội dung chỉ diễn giải lại đúng trạng thái đang có — không suy đoán nguyên nhân.
 */
function recordAlerts(martyr: MartyrData): RecordAlert[] {
  const alerts: RecordAlert[] = [];
  if (martyr.publicStatus === "RESTRICTED") {
    alerts.push({
      tone: "warning",
      message: "Hồ sơ đang ở trạng thái hạn chế",
      description:
        "Hồ sơ được đánh dấu hạn chế; một phần thông tin có thể không hiển thị đầy đủ theo quy định.",
    });
  }
  const identity = martyr.identityStatus;
  const identityName = (martyr as Record<string, unknown>).identityStatusDisplayName;
  if (identity === "UNDER_VERIFICATION") {
    alerts.push({
      tone: "info",
      message: `Tình trạng hồ sơ: ${identityStatusLabel(identity, identityName)}`,
      description:
        "Hồ sơ đang được xác minh; nội dung hiển thị có thể được cập nhật sau khi hoàn tất.",
    });
  } else if (identity === "NO_GRAVE_INFO") {
    alerts.push({
      tone: "warning",
      message: `Tình trạng hồ sơ: ${identityStatusLabel(identity, identityName)}`,
      description: "Hồ sơ chưa ghi nhận thông tin mộ; chưa thể đối chiếu với hồ sơ an táng.",
    });
  } else if (identity === "GRAVE_INFO_INCOMPLETE") {
    alerts.push({
      tone: "warning",
      message: `Tình trạng hồ sơ: ${identityStatusLabel(identity, identityName)}`,
      description: "Thông tin mộ của hồ sơ đang thiếu; cần bổ sung để đối chiếu đầy đủ.",
    });
  }
  return alerts;
}

function isOwnerlessTab(
  tab: MartyrTabKey,
): tab is "search-missions" | "dna" | "public-history" {
  return tab === "search-missions" || tab === "dna" || tab === "public-history";
}

/** Bảng Nguồn hồ sơ — chỉ bốn cột của wire, không lộ định danh tài liệu thô. */
function DocumentsTable({ rows }: { readonly rows: DocumentRow[] }) {
  const columns: ColumnsType<DocumentRow> = [
    {
      title: "Vai trò tài liệu",
      dataIndex: "documentRole",
      key: "documentRole",
      render: (value: string, record: DocumentRow) =>
        documentRoleLabel(value, (record as Record<string, unknown>).documentRoleDisplayName),
    },
    {
      title: "Loại tài liệu",
      dataIndex: "documentType",
      key: "documentType",
      render: (value: string, record: DocumentRow) =>
        documentTypeLabel(value, (record as Record<string, unknown>).documentTypeDisplayName),
    },
    {
      title: "Tên tài liệu",
      dataIndex: "label",
      key: "label",
      render: (value: string | null | undefined) => textOrDash(value),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      render: (value: string | null | undefined) => textOrDash(value),
    },
  ];
  return (
    <Table<DocumentRow>
      rowKey={(_row, index) => String(index ?? 0)}
      size="small"
      columns={columns}
      dataSource={rows}
      pagination={false}
    />
  );
}

/** Bảng Thân nhân — không có id người/counterpart trên wire nên không thể lộ. */
function RelativesTable({ rows }: { readonly rows: RelativeRow[] }) {
  const columns: ColumnsType<RelativeRow> = [
    {
      title: "Quan hệ",
      dataIndex: "relationshipType",
      key: "relationshipType",
      render: (value: string, record: RelativeRow) =>
        relationshipTypeLabel(value, (record as Record<string, unknown>).relationshipTypeDisplayName),
    },
    { title: "Họ tên", dataIndex: "displayName", key: "displayName" },
    {
      title: "Hiệu lực từ",
      dataIndex: "validFrom",
      key: "validFrom",
      render: (value: string | null | undefined) => formatDateVn(value),
    },
    {
      title: "Hiệu lực đến",
      dataIndex: "validTo",
      key: "validTo",
      render: (value: string | null | undefined) => formatDateVn(value),
    },
  ];
  return (
    <Table<RelativeRow>
      rowKey={(_row, index) => String(index ?? 0)}
      size="small"
      columns={columns}
      dataSource={rows}
      pagination={false}
    />
  );
}

/** Bảng Mộ — lịch sử liên kết đã áp scope; `linkType`/`confidenceStatus` dùng nhãn nghiệp vụ. */
function GravesTable({ rows }: { readonly rows: GraveRow[] }) {
  const columns: ColumnsType<GraveRow> = [
    {
      title: "Loại liên kết",
      dataIndex: "linkType",
      key: "linkType",
      render: (value: string, record: GraveRow) => (
        <StatusBadge
          tone={linkTypeTone(value)}
          label={linkTypeLabel(value, (record as Record<string, unknown>).linkTypeDisplayName)}
        />
      ),
    },
    {
      title: "Độ tin cậy",
      dataIndex: "confidenceStatus",
      key: "confidenceStatus",
      render: (value: string, record: GraveRow) =>
        confidenceStatusLabel(value, (record as Record<string, unknown>).confidenceStatusDisplayName),
    },
    {
      title: "Hiệu lực từ",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      render: (value: string | null | undefined) => formatDateVn(value),
    },
    {
      title: "Hiệu lực đến",
      dataIndex: "effectiveTo",
      key: "effectiveTo",
      render: (value: string | null | undefined) => formatDateVn(value),
    },
    {
      title: "Mã mộ",
      dataIndex: "graveCode",
      key: "graveCode",
      render: (graveCode: string) =>
        graveCode ? (
          <Link
            to={`/graves/${encodeURIComponent(graveCode)}`}
            aria-label={`Xem thông tin mộ ${graveCode}`}
          >
            {graveCode}
          </Link>
        ) : (
          DASH
        ),
    },
    { title: "Nghĩa trang", dataIndex: "cemeteryName", key: "cemeteryName" },
    {
      // Cột thao tác: `MartyrGraveTabRow` cố ý KHÔNG mang `graveId` thô, nên đích duy
      // nhất tới được là mã mộ — `GraveDetailPage` tự phân nhánh UUID/mã. `href` +
      // `target="_blank"` của antd render ra thẻ `<a>` thật, nên trình duyệt mở tab
      // mới; `Link` của router sẽ bị chặn lại trong SPA.
      title: "Thao tác",
      key: "actions",
      render: (_value: unknown, record: GraveRow) =>
        record.graveCode ? (
          <Button
            size="small"
            href={`/graves/${encodeURIComponent(record.graveCode)}`}
            target="_blank"
            rel="noreferrer"
            aria-label={`Xem mộ ${record.graveCode}`}
          >
            Xem mộ
          </Button>
        ) : (
          DASH
        ),
    },
  ];
  return (
    <Table<GraveRow>
      rowKey={(_row, index) => String(index ?? 0)}
      size="small"
      columns={columns}
      dataSource={rows}
      pagination={false}
    />
  );
}

/** Render dữ liệu tab theo ĐÚNG variant của union — không cast sang `any`/`object`. */
function TabRows({ data }: { readonly data: OwnedTabData }) {
  switch (data.tab) {
    case "documents":
      return <DocumentsTable rows={data.rows} />;
    case "relatives":
      return <RelativesTable rows={data.rows} />;
    case "graves":
      return <GravesTable rows={data.rows} />;
    default:
      return null;
  }
}

/**
 * Pane một tab có nguồn dữ liệu: mount mới nạp (lazy), request mang `signal` nên bị hủy
 * khi pane unmount (đổi tab) hoặc khi `martyrId`/`tab` đổi.
 */
function MartyrTabPane({ martyrId, tab }: {
  readonly martyrId: string;
  readonly tab: OwnedTabKey;
}) {
  const { state, refetch } = useApiResource(
    (signal) => getMartyrTab(martyrId, tab, signal),
    [martyrId, tab],
  );

  if (state.status === "loading") {
    return (
      <div role="status" aria-label={`Đang tải dữ liệu ${TAB_LABELS[tab]}`}>
        <LoadingSkeleton rows={3} />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <ErrorState
        title="Không thể tải nhóm thông tin này"
        description="Dữ liệu có thể chưa đồng bộ. Thử lại hoặc quay lại sau."
        {...(state.requestId !== undefined ? { requestId: state.requestId } : {})}
        onRetry={refetch}
      />
    );
  }
  if (state.status === "forbidden") {
    return (
      <ForbiddenState description="Nhóm thông tin này không khả dụng trong phạm vi được cấp." />
    );
  }

  const data =
    state.status === "ready" || state.status === "refreshing" || state.status === "stale"
      ? state.data
      : null;
  if (data === null) {
    return null;
  }

  const body: ReactNode =
    data.rows.length === 0 ? (
      <EmptyState
        title="Chưa có dữ liệu cho nhóm thông tin này"
        description="Hồ sơ chưa phát sinh bản ghi nào cho nhóm thông tin này."
      />
    ) : (
      <TabRows data={data} />
    );

  return (
    <>
      {state.status === "stale" ? (
        <div className="martyrs-tab-zone">
          <StaleState onRefresh={refetch} />
        </div>
      ) : null}
      {body}
    </>
  );
}

/**
 * Pane ba tab chưa có chủ sở hữu dữ liệu. Hiển thị trạng thái trống trung thực và
 * KHÔNG gọi API: contract cố ý không khai báo row shape cho các tab này.
 */
function UnavailableTabPane({ tab }: { readonly tab: MartyrTabKey }) {
  return (
    <EmptyState
      title="Chưa có nguồn dữ liệu cho nhóm thông tin này"
      description={`Nhóm "${TAB_LABELS[tab]}" chưa có nguồn dữ liệu được cung cấp; hệ thống không hiển thị dữ liệu suy diễn.`}
    />
  );
}

/** Giá trị form đề xuất liên kết — người dùng chỉ cung cấp bốn field, không có loại liên kết. */
type CandidateFormValues = Readonly<{
  graveId: string;
  confidenceStatus: CreateGraveMartyrLinkInput["confidenceStatus"];
  /** DatePicker trả dayjs; đổi sang ISO `yyyy-mm-dd` đúng lúc gửi API. */
  effectiveFrom: Dayjs;
  evidenceRef?: string;
}>;

/**
 * Modal đề xuất liên kết mộ của liệt sĩ đang mở (progressive, trên lớp tổng quan).
 *
 * Người dùng cung cấp mộ đích, độ tin cậy, ngày hiệu lực và tham chiếu bằng chứng.
 * **Không có control loại liên kết**: adapter `createGraveMartyrLink` tự khóa cứng
 * `SUSPECTED` nên UI không thể tạo `CONFIRMED`/`COLLECTIVE` (năng lực đó thuộc workflow
 * nội bộ S13/DNA-07). Nút gửi chỉ hiện khi người dùng có `DATA_WRITE` đúng scope bản ghi
 * mộ đã nhập; backend vẫn deny độc lập.
 */
function CandidateLinkModal({ martyr, actor, onClose, onCreated }: {
  readonly martyr: MartyrData;
  readonly actor: CurrentUser | null;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}) {
  const { options: confidenceOptions } = useCatalog("CONFIDENCE_STATUS");
  const [form] = Form.useForm<CandidateFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<
    Readonly<{ message: string; requestId?: string | undefined }> | null
  >(null);

  const watchedGraveId = Form.useWatch("graveId", form) ?? "";
  const graveId = watchedGraveId.trim();
  const mayWriteGrave =
    actor !== null && graveId !== "" && can(actor, "DATA_WRITE", { type: "GRAVE", id: graveId });
  const blocked = graveId !== "" && actor !== null && !mayWriteGrave;

  const submit = async (values: CandidateFormValues): Promise<void> => {
    setSubmitting(true);
    setFailure(null);
    const evidenceRef = values.evidenceRef?.trim() ?? "";
    try {
      await createGraveMartyrLink(
        values.graveId.trim(),
        {
          martyrProfileId: martyr.martyrId,
          confidenceStatus: values.confidenceStatus,
          effectiveFrom: values.effectiveFrom.format("YYYY-MM-DD"),
          ...(evidenceRef !== "" ? { evidenceRef } : {}),
        },
        globalThis.crypto.randomUUID(),
      );
      onCreated();
    } catch (error: unknown) {
      const requestId = error instanceof ApiProblem ? error.problem.requestId : undefined;
      setFailure({
        message:
          "Không thể ghi nhận đề xuất liên kết mộ. Vui lòng kiểm tra lại thông tin hoặc thử lại sau.",
        ...(requestId !== undefined ? { requestId } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      title="Đề xuất liên kết mộ"
      onCancel={onClose}
      // Nút gửi nằm TRONG <form> nên submit đi qua đường native, không phụ thuộc
      // thứ tự mount của FormInstance; footer mặc định bị bỏ để tránh hai nguồn submit.
      footer={null}
    >
      <Typography.Paragraph type="secondary">
        {`Liệt sĩ ${martyr.displayName} (${martyr.martyrCode}). Đề xuất được ghi nhận ở mức nghi vấn và chờ xác minh.`}
      </Typography.Paragraph>

      {failure ? (
        <Alert
          type="error"
          showIcon
          role="alert"
          message={failure.message}
          description={failure.requestId ? `Mã hỗ trợ: ${failure.requestId}` : undefined}
          style={{ marginBottom: "var(--ncc-spacing-sm)" }}
        />
      ) : null}

      <Form<CandidateFormValues>
        form={form}
        layout="vertical"
        requiredMark
        onFinish={(values) => {
          void submit(values);
        }}
      >
        <Form.Item
          name="graveId"
          label="Mã định danh mộ"
          rules={[{ required: true, message: "Nhập mã định danh mộ cần liên kết." }]}
        >
          <Input placeholder="Dán mã định danh mộ do hệ thống cung cấp" />
        </Form.Item>
        <Form.Item
          name="confidenceStatus"
          label="Độ tin cậy"
          rules={[{ required: true, message: "Chọn độ tin cậy của đề xuất." }]}
        >
          <Select
            placeholder="Chọn độ tin cậy"
            options={[...confidenceOptions]}
          />
        </Form.Item>
        <Form.Item
          name="effectiveFrom"
          label="Hiệu lực từ ngày"
          rules={[{ required: true, message: "Chọn ngày hiệu lực." }]}
        >
          <DatePicker
            format={DATE_PICKER_MASK_FORMAT}
            placeholder="DD-MM-YYYY"
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item name="evidenceRef" label="Tham chiếu bằng chứng">
          <Input placeholder="Số hồ sơ hoặc ký hiệu bằng chứng (không bắt buộc)" />
        </Form.Item>

        {blocked ? (
          <Alert
            type="warning"
            showIcon
            message="Không thể ghi nhận liên kết trên mộ này"
            description="Bạn không có quyền ghi nhận liên kết trên mộ này."
            style={{ marginBottom: "var(--ncc-spacing-sm)" }}
          />
        ) : null}

        <Flex justify="flex-end" gap="small">
          <Button onClick={onClose}>Hủy</Button>
          {blocked ? null : (
            <Button type="primary" htmlType="submit" loading={submitting}>
              Gửi đề xuất
            </Button>
          )}
        </Flex>
      </Form>
    </Modal>
  );
}

/**
 * Một field của phiếu tổng quan: nhãn TRÊN, giá trị DƯỚI — mắt quét theo cột, không phải
 * đọc ziczac nhãn|giá trị của bảng `Descriptions` cũ.
 */
function OverviewField({ label, children }: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="martyrs-field">
      <span className="martyrs-field-label">{label}</span>
      <span className="martyrs-field-value">{children}</span>
    </div>
  );
}

/** Nhóm field nghiệp vụ: title nhóm + lưới field; nhóm sau ngăn cách bằng hairline. */
function OverviewGroup({ title, children }: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="martyrs-group">
      <h3 className="martyrs-group-title">{title}</h3>
      <div className="martyrs-field-grid">{children}</div>
    </div>
  );
}

export function MartyrDetailPage() {
  // `id` là UUID đường dẫn (định tuyến kỹ thuật) — không hiển thị, không suy diễn.
  const { id = "" } = useParams<{ id: string }>();
  // Trạng thái HTTP của lượt lỗi gần nhất — cần để phân biệt "không tìm thấy" với lỗi hệ
  // thống, vì `useApiResource` chỉ trả về `error` chung. Ref không gây re-render: giá trị
  // được ghi trước khi `useApiResource` chuyển state sang `error`.
  const lastErrorStatus = useRef<number | null>(null);
  const { state, refetch } = useApiResource(
    (signal) => {
      lastErrorStatus.current = null;
      return getMartyr(id, signal).catch((error: unknown) => {
        lastErrorStatus.current = error instanceof ApiProblem ? error.status : null;
        throw error;
      });
    },
    [id],
  );

  const [activeTab, setActiveTab] = useState<MartyrTabKey | null>(null);
  const [candidateOpen, setCandidateOpen] = useState(false);
  const [created, setCreated] = useState(false);

  const actorState = useActorContext();
  const actor: CurrentUser | null =
    actorState.status === "ready" || actorState.status === "refreshing" || actorState.status === "stale"
      ? actorState.actor
      : null;
  // Quyền hiển thị action — backend vẫn deny trực tiếp; không nhánh nào dựa trên roleCode.
  const mayProposeLink = actor !== null && can(actor, "DATA_WRITE", { type: "GRAVE" });

  const martyr =
    state.status === "ready" || state.status === "refreshing" || state.status === "stale"
      ? state.data
      : null;
  const notFound = state.status === "error" && lastErrorStatus.current === 404;

  const onTabChange = (key: string): void => {
    const next = MARTYR_TAB_ORDER.find((candidate) => candidate === key);
    if (next !== undefined) setActiveTab(next);
  };

  const burial = (() => {
    if (martyr === null) return DASH;
    const cemeteryName = martyr.cemeteryName?.trim() ?? "";
    const graveCode = martyr.graveCode?.trim() ?? "";
    if (cemeteryName === "" && graveCode === "") return DASH;
    return (
      <span className="s11-cell-stack">
        <span>{cemeteryName !== "" ? cemeteryName : "Chưa có thông tin nghĩa trang"}</span>
        {graveCode !== "" ? (
          <span className="s11-cell-sub">
            Mã mộ:{" "}
            <Link
              to={`/graves/${encodeURIComponent(graveCode)}`}
              aria-label={`Xem thông tin mộ ${graveCode}`}
            >
              {graveCode}
            </Link>
          </span>
        ) : null}
      </span>
    );
  })();

  const evidence = martyr?.evidenceDocuments ?? [];

  return (
    <div className="martyrs-page">
      <Typography.Paragraph className="martyrs-back">
        <Link to="/martyrs">← Danh sách liệt sĩ</Link>
      </Typography.Paragraph>

      {/* Dải danh tính: tên + mã + badge trong MỘT khối, cụm hành động bám phải. Màn này
          không dùng `PageHeading` vì badge phải nằm cùng dòng định danh, không nằm dưới tiêu đề. */}
      <header className="martyrs-header-band">
        <div className="martyrs-band-main">
          <Typography.Title level={1} className="martyrs-band-title">
            {martyr !== null ? martyr.displayName : "Hồ sơ liệt sĩ"}
          </Typography.Title>
          <div className="martyrs-band-meta">
            <Typography.Text type="secondary">
              {martyr !== null
                ? `Mã liệt sĩ ${martyr.martyrCode}`
                : "Hồ sơ liệt sĩ trong phạm vi dữ liệu được cấp."}
            </Typography.Text>
            {martyr !== null ? (
              <>
                <StatusBadge
                  tone={identityStatusTone(martyr.identityStatus)}
                  label={identityStatusLabel(martyr.identityStatus, (martyr as Record<string, unknown>).identityStatusDisplayName)}
                />
                {martyr.publicStatus !== undefined ? (
                  <StatusBadge
                    tone={martyr.publicStatus === "RESTRICTED" ? "warning" : "neutral"}
                    label={publicStatusLabel(martyr.publicStatus, (martyr as Record<string, unknown>).publicStatusDisplayName)}
                  />
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        <Flex className="martyrs-band-actions" gap="small" wrap>
          {/* "Làm mới" KHÔNG phụ thuộc actor: hồ sơ hoặc actor lỗi vẫn phải có đường thử lại. */}
          <Button
            icon={<ReloadOutlined />}
            aria-label="Làm mới"
            loading={state.status === "refreshing"}
            onClick={refetch}
          >
            Làm mới
          </Button>
          {/* Đề xuất liên kết mộ thuộc lớp mặc định (không nằm sau tab), chỉ render khi actor
              có `DATA_WRITE` scope GRAVE; backend vẫn deny độc lập. */}
          {mayProposeLink ? (
            <Button type="primary" onClick={() => { setCandidateOpen(true); }}>
              Đề xuất liên kết mộ
            </Button>
          ) : null}
        </Flex>
      </header>

      {/* Cảnh báo pháp lý/nghi vấn thuộc lớp mặc định — không bao giờ nằm sau tab. */}
      {martyr !== null
        ? recordAlerts(martyr).map((alert) => (
            <Alert
              key={alert.message}
              type={alert.tone}
              showIcon
              message={alert.message}
              description={alert.description}
              style={{ marginBottom: "var(--ncc-spacing-sm)" }}
            />
          ))
        : null}

      {created ? (
        <Alert
          type="success"
          showIcon
          message="Đã ghi nhận đề xuất liên kết mộ"
          description="Liên kết đang ở mức nghi vấn và sẽ được xác minh theo quy trình trước khi có hiệu lực."
          style={{ marginBottom: "var(--ncc-spacing-sm)" }}
        />
      ) : null}

      {state.status === "refreshing" ? (
        <Typography.Text
          type="secondary"
          role="status"
          aria-label="Đang làm mới hồ sơ liệt sĩ"
          className="martyrs-refresh-note"
        >
          Đang làm mới hồ sơ liệt sĩ…
        </Typography.Text>
      ) : null}

      {state.status === "stale" ? (
        <div className="martyrs-refresh-note">
          <StaleState
            {...(state.fetchedAt !== undefined
              ? { lastSyncedAt: formatDateTimeVn(state.fetchedAt) }
              : {})}
            onRefresh={refetch}
          />
        </div>
      ) : null}

      {state.status === "loading" ? (
        <div role="status" aria-label="Đang tải hồ sơ liệt sĩ" className="martyrs-zone">
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}

      {state.status === "error" && !notFound ? (
        <div className="martyrs-zone">
          <ErrorState
            title="Không thể tải hồ sơ liệt sĩ"
            description="Kiểm tra kết nối hoặc thử lại sau ít phút."
            {...(state.requestId !== undefined ? { requestId: state.requestId } : {})}
            onRetry={refetch}
          />
        </div>
      ) : null}

      {notFound ? (
        <div className="martyrs-zone">
          <EmptyState
            title="Không tìm thấy hồ sơ liệt sĩ"
            description="Hồ sơ không tồn tại hoặc không còn trong phạm vi quản lý."
            action={<Link to="/martyrs">Về danh sách liệt sĩ</Link>}
          />
        </div>
      ) : null}

      {state.status === "forbidden" ? (
        <div className="martyrs-zone">
          <ForbiddenState description="Hồ sơ không khả dụng trong phạm vi dữ liệu được cấp." />
        </div>
      ) : null}

      {martyr !== null ? (
        <>
          <section className="martyrs-detail-panel" aria-label="Tổng quan hồ sơ">
            <h2 className="martyrs-panel-title">Tổng quan hồ sơ</h2>

            <OverviewGroup title="Quân ngũ">
              <OverviewField label="Cấp bậc">{textOrDash(martyr.rankName)}</OverviewField>
              <OverviewField label="Chức vụ">{textOrDash(martyr.positionName)}</OverviewField>
              <OverviewField label="Đơn vị">{textOrDash(martyr.unitName)}</OverviewField>
              <OverviewField label="Ngày tham gia">
                {formatDateVn(martyr.participationDate)}
              </OverviewField>
            </OverviewGroup>

            <OverviewGroup title="Hy sinh">
              <OverviewField label="Ngày hy sinh">
                {formatDateVn(martyr.sacrificeDate)}
              </OverviewField>
              <OverviewField label="Nơi hy sinh">
                {textOrDash(martyr.sacrificePlace)}
              </OverviewField>
              <OverviewField label="Hoàn cảnh hy sinh">
                {martyr.sacrificeCircumstance === undefined ? (
                  // Trường vắng khỏi JSON nghĩa là người gọi thiếu quyền đọc trường nhạy cảm —
                  // nói thật là không có quyền, KHÔNG bịa giá trị và không báo lỗi.
                  <span className="martyrs-masked">
                    <LockOutlined aria-hidden="true" />
                    <Typography.Text type="secondary">
                      Bạn không có quyền xem thông tin này
                    </Typography.Text>
                  </span>
                ) : (
                  textOrDash(martyr.sacrificeCircumstance)
                )}
              </OverviewField>
            </OverviewGroup>

            <OverviewGroup title="An táng & ghi công">
              <OverviewField label="Nơi an táng ban đầu">
                {textOrDash(martyr.initialBurialPlace)}
              </OverviewField>
              <OverviewField label="An táng">{burial}</OverviewField>
              <OverviewField label="Số bằng Tổ quốc ghi công">
                {textOrDash(martyr.fatherlandMeritNo)}
              </OverviewField>
            </OverviewGroup>

            <OverviewGroup title="Bằng chứng kèm hồ sơ">
              {evidence.length === 0 ? (
                <Typography.Text type="secondary">
                  Chưa ghi nhận bằng chứng kèm hồ sơ.
                </Typography.Text>
              ) : (
                <ul className="martyrs-evidence-list">
                  {evidence.map((document, index) => (
                    <li className="martyrs-evidence-item" key={`${document.documentType}-${index}`}>
                      <span>{textOrDash(document.label)}</span>
                      <span className="martyrs-evidence-type">
                        {textOrDash(document.documentType)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </OverviewGroup>
          </section>

          <section className="martyrs-detail-panel martyrs-tabs" aria-label="Nhóm thông tin chi tiết">
            <Tabs
              // Chưa mở tab nào: giá trị điều khiển không khớp item nào ⇒ zero request.
              activeKey={activeTab ?? NO_TAB_ACTIVE}
              onChange={onTabChange}
              // Đổi tab sẽ unmount pane cũ ⇒ hủy request đang chạy (không rò kết quả muộn).
              destroyOnHidden
              items={MARTYR_TAB_ORDER.map((key) => ({
                key,
                label: TAB_LABELS[key],
                children: isOwnerlessTab(key) ? (
                  <UnavailableTabPane tab={key} />
                ) : (
                  <MartyrTabPane martyrId={id} tab={key} />
                ),
              }))}
            />
            {activeTab === null ? (
              <Typography.Paragraph type="secondary" className="martyrs-tab-hint">
                Chọn một nhóm thông tin để xem.
              </Typography.Paragraph>
            ) : null}
          </section>

          {candidateOpen ? (
            <CandidateLinkModal
              martyr={martyr}
              actor={actor}
              onClose={() => { setCandidateOpen(false); }}
              onCreated={() => {
                setCandidateOpen(false);
                setCreated(true);
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
